import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import localForage from 'localforage';
import {
  getHealthCareWorkerByUserId,
  getHealthCareWorkerPoints,
  getHealthCareWorkerPointsTodoItems,
  getHealthCareWorkerTeamStanding,
  getMoreInformation,
  updateHealthCareWorker,
  updateHealthCareWorkerTabs,
  updateHealthCareWorkerWelcomeMessage,
} from './healthCareWorker.actions';
import { HealthCareWorkerState } from './healthCareWorker.types';
import { setFulfilledThunkActionStatus, setThunkActionStatus } from '../utils';
import { UpdateHealthCareWorkerInputModelInput } from '@ecdlink/graphql';

const initialState: HealthCareWorkerState = {
  healthCareWorker: undefined,
  points: { completedItems: [] },
};

const healthCareWorkerSlice = createSlice({
  name: 'healthCareWorker',
  initialState,
  reducers: {
    // Full wipe of HCW + points so a different login cannot keep the previous CHW bag
    resetHealthCareWorkerState: () => initialState,
    updateHealthCareWorker: (
      state,
      action: PayloadAction<UpdateHealthCareWorkerInputModelInput>
    ) => {
      if (state.healthCareWorker) {
        state.healthCareWorker = {
          ...state.healthCareWorker,
          isRegistered: action.payload.isRegistered,
          languageId: action.payload.languageId,
        };
      }
    },
    setHCWTabClicked: (
      state,
      action: PayloadAction<{
        tab:
          | 'dashboardclient'
          | 'dashboardclientb'
          | 'dashboardhighlights'
          | 'dashboardvisits'
          | 'visits'
          | 'progress'
          | 'referrals'
          | 'contact';
        clicked: boolean;
        isOnline: boolean;
      }>
    ) => {
      const { tab, clicked, isOnline } = action.payload;
      if (!state.healthCareWorker) return;

      // Only act when we're being told the tab was actually viewed/clicked
      if (!clicked) return; // ← explicit: ignore "unclick" events

      let shouldMarkSynced = false;

      switch (tab) {
        case 'dashboardclient':
          if (!state.healthCareWorker.clickedDashboardClientsTab) {
            state.healthCareWorker.clickedDashboardClientsTab = true;
            shouldMarkSynced = true;
          }
          break;
        case 'dashboardclientb':
          if (!state.healthCareWorker.clickedDashboardClientsTabB) {
            state.healthCareWorker.clickedDashboardClientsTabB = true;
            shouldMarkSynced = true;
          }
          break;
        case 'dashboardhighlights':
          if (!state.healthCareWorker.clickedDashboardHighlightsTab) {
            state.healthCareWorker.clickedDashboardHighlightsTab = true;
            shouldMarkSynced = true;
          }
          break;
        case 'dashboardvisits':
          if (!state.healthCareWorker.clickedDashboardVisitsTab) {
            state.healthCareWorker.clickedDashboardVisitsTab = true;
            shouldMarkSynced = true;
          }
          break;
      }
      if (shouldMarkSynced) {
        state.healthCareWorker.synced = isOnline;
      }
    },
  },
  extraReducers: (builder) => {
    setThunkActionStatus(builder, updateHealthCareWorkerWelcomeMessage);
    setThunkActionStatus(builder, updateHealthCareWorker);
    setThunkActionStatus(builder, getHealthCareWorkerPoints);
    setThunkActionStatus(builder, getHealthCareWorkerTeamStanding);
    setThunkActionStatus(builder, getMoreInformation);
    setThunkActionStatus(builder, getHealthCareWorkerPointsTodoItems);
    builder.addCase(
      getHealthCareWorkerPointsTodoItems.fulfilled,
      (state, action) => {
        state.points = {
          ...state.points,
          todoItems: action.payload,
        };

        setFulfilledThunkActionStatus(state, action);
      }
    );
    builder.addCase(getMoreInformation.fulfilled, (state, action) => {
      const locale = action?.meta?.arg?.locale;

      if (!state.points?.infoPage) {
        state.points.infoPage = [];
      }

      const existingLocaleIndex = state.points.infoPage.findIndex((infoItem) =>
        Object.hasOwnProperty.call(infoItem, locale)
      );

      if (existingLocaleIndex !== -1) {
        state.points.infoPage[existingLocaleIndex][locale] = {
          dateLoaded: new Date().toISOString(),
          data: action.payload,
        };
      } else {
        const newLocaleData = {
          [locale]: {
            dateLoaded: new Date().toISOString(),
            data: action.payload,
          },
        };
        state.points.infoPage.push(newLocaleData);
      }

      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getHealthCareWorkerByUserId.fulfilled, (state, action) => {
      state.healthCareWorker = action.payload;
    });
    builder.addCase(getHealthCareWorkerPoints.fulfilled, (state, action) => {
      const incoming = action.payload; // Array of your items

      if (!incoming || incoming.length === 0) {
        setFulfilledThunkActionStatus(state, action);
        return;
      }

      // Build Map with composite key: `${pointsActivityId}-${year}-${month}`
      const incomingMap = new Map<string, (typeof incoming)[0]>();

      for (const item of incoming) {
        const key = `${item.pointsActivityId}-${item.year}-${item.month}`;
        incomingMap.set(key, item); // Latest data wins (overrides if duplicate)
      }

      // Filter out old versions of items that are being updated
      const preservedItems = state.points.completedItems.filter((existing) => {
        const key = `${existing.pointsActivityId}-${existing.year}-${existing.month}`;
        return !incomingMap.has(key);
      });

      // Combine preserved + all new/updated incoming items
      const updatedItems = [...preservedItems, ...incoming];

      // Optional: Sort by year/month descending (latest first)
      updatedItems.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        if (a.month !== b.month) return b.month - a.month;
        return b.pointsTotal - a.pointsTotal; // or by activity name, etc.
      });

      state.points.completedItems = updatedItems;

      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(
      getHealthCareWorkerTeamStanding.fulfilled,
      (state, action) => {
        state.teamStanding = action.payload;

        setFulfilledThunkActionStatus(state, action);
      }
    );
    builder.addCase(updateHealthCareWorker.fulfilled, (state, action) => {
      state.healthCareWorker = {
        ...state.healthCareWorker,
        ...action.payload,
      };

      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(updateHealthCareWorkerTabs.fulfilled, (state, action) => {
      state.healthCareWorker = action.payload;
      // TODO: only need to update the clicked tabs here
    });
    builder.addCase(
      updateHealthCareWorkerWelcomeMessage.fulfilled,
      (state, action) => {
        state.healthCareWorker = {
          ...state.healthCareWorker,
          ...action.payload,
        };

        setFulfilledThunkActionStatus(state, action);
      }
    );
  },
});

const { reducer: healthCareWorkerReducer, actions: healthCareWorkerActions } =
  healthCareWorkerSlice;

const healthCareWorkerPersistConfig = {
  key: 'healthCareWorker',
  storage: localForage,
  blacklist: [],
};

export {
  healthCareWorkerPersistConfig,
  healthCareWorkerReducer,
  healthCareWorkerActions,
};
